<template>
  <div class="product" @click="to">
    <div class="product-image">
      <img
        v-lazy="limitImgUrl(product.image || defaultImg, 150)"
        style="border-radius: 8px"
        width="130px"
        height="130px"
      />
      <div v-if="!product.inStock" class="product-instock">
        <q-icon name="app:habisIcon" size="80px" />
      </div>
    </div>
    <div v-if="product && product.id" class="product-info" :class="{ disabled: !product.inStock }">
      <h3 bold class="product-name">
        {{ product.name }}
      </h3>
      <h5 class="product-desc">
        {{ product.desc }}
      </h5>
      <h4 bold class="product-price">
        <div v-if="promo" break class="product-price-del">
          {{ $pid(product.listPrice) }}
        </div>
        <div break class="product-price-normal">
          <span>{{ $n(product.finalPrice, 'currency', 'id-ID') }}</span>
          <span v-if="promo" class="tag">
            <i class="iconfont dm-Tag" /> {{ $t('list.promo') }}
          </span>
        </div>
      </h4>
      <div class="product-add" @click.stop="onDirectToCart">
        <!--已加购的显示加购数量-->
        <div v-if="cartIds.includes(props.product.id)" class="product-number">{{ props.cartCount[props.product.id] > 99 ? '99+' : props.cartCount[props.product.id] }}</div>
        <!--未加购的显示加购符号-->
        <i v-else class="iconfont dm-Add1" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue';
import { ADD_SUCCESS_RECOMMEND } from 'src/utils/config';
import { bus } from 'src/boot/global';
import { useRouter } from 'src/utils/v2_helper/router';
import { useLoading } from 'src/utils/hooks/loading';
import { isPkg } from 'src/pages/checkout/utils';
import { use18n, useIns } from 'src/utils/v2_helper/common';
import { reqAddToCart } from 'src/api/fullDineIn';
import { getAddToCart } from 'src/api/cart';
import { getCartTableId, getTable, limitImgUrl } from 'src/utils/common';

const defaultImg = 'https://static.bakmigm.co.id/menu/00110000/Default.jpg';
const router = useRouter();
const app = useIns();
const { $t } = use18n();
const { loading, hide } = useLoading();
const props = defineProps({
  extra: {
    type: Object,
    default: () => ({}),
  },
  product: {
    type: Object,
    default: () => {},
  },
  // 加购商品的数据
  cartCount: {
    type: Object,
    default: () => {},
  }
});

const promo = computed(
  () => props.product.finalPrice !== props.product.listPrice,
);
// 加购商品的id
const cartIds = computed(() => {
  if (!props.cartCount || props.cartCount.length === 0) {
    return []
  }
  return Object.keys(props.cartCount)
})

function to() {
  router.push({
    name: 'product-detail',
    query: props.extra,
    params: { id: props.product.id },
  });
}
function onDirectToCart() {
  // 判断是否超出库存
  const cartNumber = props.cartCount[props.product.id] || 0 // 当前已加购数量
  const stock = props.product.productStock // 商品库存
  // -1表示没有库存限制
  if (stock !== -1 && cartNumber >= stock) {
    app.$message.error($t('list.stockError', [stock || 0]))
    return
  }
  loading();
  const callApi = app.isFullDineIn ? reqAddToCart : getAddToCart;
  let params = {
    type: isPkg(props.product.productType) ? 'package' : 'single',
    productId: props.product.id,
    quantity: 1,
    modifiers: [],
    baseGroup: {},
    optionGroups: [],
    comment: '',
    quickAdd: true,
    priceStamp: props.product.listPrice,
  };
  if (app.isFullDineIn) {
    const { tableId, cartId } = getCartTableId();
    params.tableId = tableId;
    params.cartId = cartId;
    params.tableNumber = getTable();
  } else {
    params = { addItem: { ...params } };
  }
  callApi(params)
    .then((res) => {
      // 321表示不可快速加购，直接跳转到详情页
      if (!res.success) {
        to();
      } else {
        bus.$emit(ADD_SUCCESS_RECOMMEND);
      }
    })
    .finally(() => {
      hide();
    });
}
</script>

<style lang="scss" scoped>

h3,
h4,
h5 {
  margin: 0;
  line-height: 1;
}
.dotline {
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
}
.dot1 {
  @extend .dotline;
  line-clamp: 1;
  -webkit-line-clamp: 1;
}
.dot2 {
  @extend .dotline;
  line-clamp: 2;
  -webkit-line-clamp: 2;
}
.dot5 {
  @extend .dotline;
  line-clamp: 5;
  -webkit-line-clamp: 5;
}
.product {
  display: flex;
  width: 100%;
  height: 154px;
  background: #fff;
  overflow: hidden;
  padding: 12px;
  margin-bottom: 1px;
  position: relative;
  &-add {
    position: absolute;
    right: 0px;
    bottom: 0px;
    font-size: 20px;
    width: 50px;
    height: 62px;
    display: flex;
    align-items: center;
    justify-content: center;
    .iconfont {
      font-size: 30px;
    }
  }
  &-number {
    display: flex;
    width: 30px;
    height: 30px;
    border: 2px solid #C63527;
    border-radius: 15px;
    box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
    background: #FFF;
    color: #C63527;
    font-size: 12px;
    font-weight: bold;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }
  &-instock {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba($color: #000000, $alpha: 0.5);
  }
  &-image {
    overflow: hidden;
    border-radius: 8px;
    width: 130px;
    height: 130px;
    position: relative;
    .image {
      background-size: cover;
    }
  }
  &-info {
    padding: 8px 0 12px 12px;
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  &-name {
    @extend .dot2;
    line-height: 1.2;
    word-break: break-word;
    font-size: 14px;
  }
  &-desc {
    @extend .dot2;
    line-height: 1.2;
    word-break: break-word;
    color: #6b7280;
    margin-top: 4px;
    font-size: 12px;
  }
  &-price {
    margin-top: auto;
    font-size: 16px;
    &-del {
      font-weight: normal;
      text-decoration: line-through;
      color: $grayFont;
      font-size: 12px;
      padding-bottom: 2px;
    }
    &-normal {
      .tag {
        white-space: nowrap;
        .iconfont {
          font-size: inherit;
        }
        font-size: 12px;
        color: #fff;
        padding: 2px 4px;
        margin-left: 8px;
        background: $primary;
        border-radius: $border-radius-in;
      }
    }
  }
}
</style>
